import { MenuItem, isModuleShortcutLabel } from '@core/models/menu.model';
import { MultiCultureModuleDto, MultiCultureSubmoduleDto } from '@core/models/user.model';

/** Matches optional `environment.externalMenuLinks` (dev may leave strings empty). */
export type ExternalMenuLinksShape = {
  attendance?: string;
  laboratoryOperations?: string;
  inventory?: string;
  financial?: string;
  urgency?: string;
};

/**
 * Replaces `{userEmail}` in a URL template with the RFC 3986–encoded address.
 */
export function resolveExternalLaunchUrl(
  template: string | undefined,
  email: string | undefined
): string | null {
  if (!template?.trim()) return null;
  const encoded = encodeURIComponent(email ?? '');
  return template.split('{userEmail}').join(encoded);
}

function withLoginHint(base: string | undefined, email: string): string | null {
  if (!base?.trim()) return null;
  const noHash = base.trim().split('#')[0];
  return `${noHash}#loginHint=${encodeURIComponent(email)}`;
}

/**
 * Resolve external launch URL: API template first; optional env fallback only when `apiOnly` is false.
 * Produção deve usar `apiOnly: true` para SUBMODULO_LANCAMENTO_EXTERNO ser a única fonte (sem URLs fixas no bundle).
 */
export function resolveExternalLaunchUrlChain(options: {
  template?: string | null;
  submoduleKey?: string | undefined;
  email?: string | undefined;
  externalMenuLinks?: ExternalMenuLinksShape;
  apiOnly: boolean;
}): string | null {
  const fromApi = resolveExternalLaunchUrl(options.template ?? undefined, options.email);
  if (fromApi) return fromApi;
  if (options.apiOnly) return null;
  return resolveExternalLaunchFromEnvironment(
    options.submoduleKey,
    options.externalMenuLinks,
    options.email,
  );
}

/**
 * Fallback when the API has no row in SUBMODULO_LANCAMENTO_EXTERNO (e.g. before migration).
 */
export function resolveExternalLaunchFromEnvironment(
  key: string | undefined,
  links: ExternalMenuLinksShape | undefined,
  email: string | undefined
): string | null {
  if (!links || !key?.trim()) return null;
  const e = email ?? '';
  const k = key.trim().toLowerCase();
  if (k === 'atendimento') return withLoginHint(links.attendance, e);
  if (k === 'operacoes_laboratoriais' || k === 'autolac') return withLoginHint(links.laboratoryOperations, e);
  if (k === 'estoque') return withLoginHint(links.inventory, e);
  if (k === 'financeiro') return withLoginHint(links.financial, e);
  if (k === 'urgencia') return withLoginHint(links.urgency || links.laboratoryOperations, e);
  // Itens legados do workspace: mesmo host group WMI que Operações Laboratoriais (evita 0a4eee1d / “Autolac” errado).
  if (k === 'interlac' || k === 'confere' || k === 'laudos_web' || k === 'integre')
    return withLoginHint(links.laboratoryOperations, e);
  return null;
}

/**
 * Raw backend routes that should NOT be mapped to SPA paths — they're
 * "jump" routes the sidebar (and quick-access) hand off to the AVD client
 * or a module-specific external URL. Same list the sidebar uses when
 * `module.key === 'workspace_laboratorial'` to skip the mapping.
 */
export function isWorkspaceLaboratorialJumpRoute(route: string | undefined): boolean {
  if (!route) return false;
  const r = route.trim().toLowerCase().split('?')[0];
  return (
    r === '/atendimento' ||
    r === '/autolac' ||
    r === '/estoque' ||
    r === '/financeiro' ||
    r === '/urgencia' ||
    r === '/interlac' ||
    r === '/confere' ||
    r === '/laudos-web' ||
    r === '/integre'
  );
}

/** Used in the flyout to show an external-link affordance on “Módulo …” shortcuts that launch AVD. */
export function menuItemHasExternalLaunchVisual(item: MenuItem): boolean {
  if (item.externalLaunchUrlTemplate?.trim()) return true;
  if (!isModuleShortcutLabel(item.labelKey ?? item.label)) return false;
  return isWorkspaceLaboratorialJumpRoute(item.route);
}

/** Pair pulled from the modules cache when the quick-access payload omits these fields. */
export interface ResolvedLaunchInfo {
  template?: string | null;
  key?: string;
}

function findSubmoduleByRoute(
  subs: MultiCultureSubmoduleDto[] | undefined,
  route: string
): MultiCultureSubmoduleDto | null {
  if (!subs) return null;
  for (const sub of subs) {
    if (sub.route && sub.route === route) return sub;
    const nested = findSubmoduleByRoute(sub.children, route);
    if (nested) return nested;
  }
  return null;
}

/**
 * Looks up `externalLaunchUrlTemplate` and `key` from the cached `/api/modules`
 * tree for a quick-access tile. The quick-access endpoint doesn't enrich
 * these fields yet, but the modules endpoint does — the sidebar (which fetches
 * `/api/modules` at app load) populates `SidebarStateService`, and the home
 * page reads from there to get the same data for the same item. Returns
 * `null` when the cache isn't ready or the entry isn't found — the caller
 * falls through to the env / AVD path as before.
 *
 * Resolution order:
 *  1. Raw BE route → most reliable join key (sidebar cache keys submodules
 *     by their raw BE route). Walks every module's submodule tree.
 *  2. moduleKey fallback — when the quick-access item carries a moduleKey,
 *     look up the module and promote its single submodule's template.
 */
export function lookupQuickAccessLaunch(
  modules: MultiCultureModuleDto[] | null | undefined,
  options: {
    rawRoute?: string;
    moduleId?: number;
    itemId?: number;
    isSubmodule?: boolean;
    moduleKey?: string | null;
    submoduleKey?: string | null;
  },
): ResolvedLaunchInfo | null {
  if (!modules || modules.length === 0) return null;

  // 1) Raw BE route — most reliable, survives key/id mismatches.
  if (options.rawRoute) {
    for (const module of modules) {
      const sub = findSubmoduleByRoute(module.submodules, options.rawRoute);
      if (sub) return { template: sub.externalLaunchUrlTemplate, key: sub.key };
    }
  }

  // 2) moduleKey lookup — key-based identification from the multi-culture API.
  if (options.moduleKey) {
    const module = modules.find(m => m.key === options.moduleKey);
    if (!module) return null;
    if (options.submoduleKey) {
      const sub = findSubmoduleByKeyRecursive(module.submodules, options.submoduleKey);
      if (sub) return { template: sub.externalLaunchUrlTemplate, key: sub.key };
      return null;
    }
    const single = module.submodules?.length === 1 ? module.submodules[0] : null;
    return {
      template: single?.externalLaunchUrlTemplate,
      key: module.key ?? single?.key,
    };
  }

  return null;
}

function findSubmoduleByKeyRecursive(
  subs: MultiCultureSubmoduleDto[],
  key: string
): MultiCultureSubmoduleDto | null {
  for (const sub of subs) {
    if (sub.key === key) return sub;
    const nested = findSubmoduleByKeyRecursive(sub.children, key);
    if (nested) return nested;
  }
  return null;
}
